import { clsx } from "clsx";
import { darken, readableColor, saturate, shade, tint } from "polished";
import { type FC, useRef } from "react";
import { DragPreview, mergeProps, useDrag } from "react-aria";
import { HStack, SymbolIcon } from "widgets";

import { WorkflowEditorNodeDragAndDrop } from "@/modules/workflow/components/WorkflowEditorNodeDragAndDrop";
import type { DndNodeData } from "@/modules/workflow/utils/types";

import style from "../styles/VersionTag.module.css";

interface VersionTagProps extends Pick<DndNodeData, "version"> {
  color: string | undefined | null;
  draggable?: false | null;
}

interface VersionDraggableTagProps extends DndNodeData {
  color: string | undefined | null;
  draggable: true;
}

export const VersionTag: FC<VersionTagProps | VersionDraggableTagProps> = (props) => {
  let bgColor = props.color ?? "#fff";
  let borderColor = "transparent";
  let txtColor = "#292624"; // --text-default

  try {
    const regex = /^var\(([a-z0-9-]+)\)$/;
    if (regex.test(bgColor)) {
      const variable = regex.exec(bgColor)?.[1];
      if (variable) {
        bgColor = getComputedStyle(document.documentElement).getPropertyValue(variable)?.trim();
      }
    }

    txtColor = readableColor(bgColor, shade(0.9, bgColor), tint(0.9, bgColor), false);
    borderColor = darken(0.15, saturate(0.1, bgColor));
  } catch {
    /* do nothing, will use default color: Secondary */
  }

  const preview = useRef(null);
  const { dragProps } = useDrag({
    preview,
    getItems() {
      return (props as VersionDraggableTagProps).activityName
        ? [
            {
              "text/plain": JSON.stringify({
                activityName: (props as VersionDraggableTagProps).activityName,
                version: props.version,
              } satisfies DndNodeData),
            },
          ]
        : [];
    },
  });

  if (!props.version) return null;

  return (
    <>
      <HStack
        spacing={1}
        className={clsx(style.tag, { [style.draggable]: props.draggable })}
        domProps={props.draggable ? mergeProps(dragProps, { tabIndex: 0 }) : undefined}
        style={{
          // background: bgColor,
          // color: txtColor,
          background: props.draggable ? "white" : undefined,
          ...(props.draggable
            ? { boxShadow: `inset 0 0 0 1px ${borderColor}`, outline: 0, cursor: "grab" }
            : undefined),
        }}
      >
        {props.draggable === true ? (
          <SymbolIcon icon="drag_indicator" style={{ flex: "0 0 auto", color: txtColor }} size={12} />
        ) : null}
        <SymbolIcon
          icon="deployed_code"
          filled={!!props.color}
          size={12}
          style={props.color ? { color: bgColor } : undefined}
        />
        <span className={style.text}>{props.version}</span>
      </HStack>
      {props.draggable ? (
        <DragPreview ref={preview}>
          {(items) => {
            const data: DndNodeData = JSON.parse(items[0]["text/plain"]);
            return (
              <WorkflowEditorNodeDragAndDrop
                activityName={data.activityName}
                version={data.version}
                key={data.activityName + data.version}
              />
            );
          }}
        </DragPreview>
      ) : null}
    </>
  );
};
